import Accordion from "react-bootstrap/Accordion";
import PropTypes from "prop-types";

import EprContentReportable from "./EprContentReportable";

import { getTotalCountByStandard } from "../../utils/ManageEpr/getContentCounts";

import "../../styles/ManageEprContent.scss";

/**
 * Epr Content Component
 * @name EprContent
 * @param {[]} allGrades
 * @param {func} handleAddClick
 * @param {{}} reportableContent
 * @param {[]} selectedGrades
 * @return {JSX.element}
 * @constructor
 */

// GLEs are selectable as reportable content for all subjects.

const renderChildEo = ({ cEo, gradeLevel, handleAddClick, templateExists }) => (
    <div style={{ marginLeft: "40px" }} key={`${gradeLevel}-${cEo.gradeToContentKey}`}>
        <EprContentReportable
            contentKey={cEo.gradeToContentKey}
            grade={gradeLevel}
            handleAddClick={handleAddClick}
            reportFlag={cEo.reportFlag}
            templateExists={templateExists}
            title={cEo.body}
            type="gradeEvidenceOutcomeChild"
        />
    </div>
);

const renderEo = ({ eo, gradeLevel, handleAddClick, templateExists }) => {
    const { body, evidenceOutcomeChildList, gradeToContentKey, reportFlag } = eo;
    return (
        <div style={{ marginLeft: "40px" }} key={`${gradeLevel}-${gradeToContentKey}`}>
            <EprContentReportable
                contentKey={gradeToContentKey}
                grade={gradeLevel}
                handleAddClick={handleAddClick}
                reportFlag={reportFlag}
                templateExists={templateExists}
                title={body}
                type="gradeEvidenceOutcome"
            />
            {evidenceOutcomeChildList?.length > 0 &&
                evidenceOutcomeChildList.map((cEo) =>
                    renderChildEo({ cEo, gradeLevel, handleAddClick, templateExists })
                )}
        </div>
    );
};

const renderGle = ({ gle, gradeLevel, handleAddClick, templateExists }) => {
    const { gleShortName, gleName, evidenceOutcomes, gradeToContentKey, reportFlag } = gle;
    const displayGleName = gleName.substring(gleName.indexOf(".") + 1);
    return (
        <div style={{ marginLeft: "22px" }} key={`${gradeLevel}-${gleName}`}>
            <EprContentReportable
                contentKey={gradeToContentKey}
                grade={gradeLevel}
                handleAddClick={handleAddClick}
                reportFlag={reportFlag}
                templateExists={templateExists}
                title={
                    <>
                        <b>{gleShortName}:</b> {displayGleName}
                    </>
                }
                type="gleToGradeLevelKeys"
            />
            <div style={{ marginLeft: "45px" }}>
                <b>Evidence Outcomes:</b>
            </div>
            {evidenceOutcomes.map((eo) => renderEo({ eo, gradeLevel, handleAddClick, templateExists }))}
        </div>
    );
};

const renderStandard = ({ standard, index, gradeLevel, handleAddClick, templateExists }) => {
    const { gles, gradeToContentKey, reportFlag, standardName } = standard;
    const displayStandardName = standardName.substring(standardName.indexOf(".") + 1);
    const itemPerStandardCount = getTotalCountByStandard(standard);

    return (
        <Accordion.Item
            style={{
                marginBottom: "12px",
                border: "1px solid #A7A9AC",
                borderRadius: "5px",
                overflow: "auto"
            }}
            eventKey={index}
            key={`${gradeLevel}-${standardName}`}
        >
            <EprContentReportable
                contentKey={gradeToContentKey}
                count={itemPerStandardCount}
                grade={gradeLevel}
                handleAddClick={handleAddClick}
                isAccordionButton
                reportFlag={reportFlag}
                templateExists={templateExists}
                title={
                    <>
                        <b>Standard {index + 1}:</b> {displayStandardName}
                    </>
                }
                type="gradeCdestandard"
            />
            <Accordion.Body>
                {gles.map((gle) => renderGle({ gle, gradeLevel, handleAddClick, templateExists }))}
            </Accordion.Body>
        </Accordion.Item>
    );
};

const EprContent = ({ allGrades, handleAddClick, reportableContent, selectedGrades, templateExists }) => {
    const selectedGradesWithoutContent = selectedGrades.filter(
        (grade) =>
            !reportableContent.gradeLevels.some(
                (reportable) => reportable.key === grade.key || reportable.gradeLevel === grade.gradeLevel
            )
    );

    return (
        <div className="outcomes-container">
            {reportableContent?.gradeLevels?.length > 0 &&
                reportableContent.gradeLevels.map((grade) => {
                    const { gradeLevel, cdeStandards } = grade;
                    const gradeData = allGrades?.find((g) => g.gradeLevel === gradeLevel);
                    const gradeLevelName = gradeData?.name;
                    const showGrade = selectedGrades.find((selectedGrade) => selectedGrade.gradeLevel === gradeLevel);

                    return (
                        showGrade && (
                            <div className="grade-container" key={gradeLevel}>
                                <div className="grade-name">
                                    {gradeLevelName === "K" ? "Kindergarten" : `${gradeLevelName} Grade`}
                                </div>
                                <Accordion alwaysOpen>
                                    {cdeStandards.map((standard, index) =>
                                        renderStandard({
                                            standard,
                                            index,
                                            gradeLevel,
                                            handleAddClick,
                                            templateExists
                                        })
                                    )}
                                </Accordion>
                            </div>
                        )
                    );
                })}
            {selectedGradesWithoutContent
                .sort((a, b) => Number(a.key) - Number(b.key))
                .map((grade) => (
                    <div className="grade-container" key={grade.key}>
                        <div className="grade-name">{grade.name === "K" ? "Kindergarten" : `${grade.name} Grade`}</div>
                        <div className="no-content-avail-msg">
                            No reportable content available for selected subject and grade.
                        </div>
                    </div>
                ))}
        </div>
    );
};

EprContent.propTypes = {
    allGrades: PropTypes.instanceOf(Array),
    handleAddClick: PropTypes.func.isRequired,
    reportableContent: PropTypes.oneOfType([PropTypes.object]).isRequired,
    selectedGrades: PropTypes.instanceOf(Array),
    templateExists: PropTypes.bool
};

EprContent.defaultProps = {
    allGrades: [],
    selectedGrades: [],
    templateExists: false
};

export default EprContent;
